/**
 * The browser half of a `"combatant"` prompt (`board-pick-prompts` Phase 3): a decision whose answer
 * is a unit (Impale's victim, any `choose_from_area`) is answered by clicking the unit. The eligible
 * units ring, every other unit dims, and a click on a ringed one answers with its id. The candidates
 * are the prompt's own option ids, so the pick can't offer a unit the server won't accept.
 *
 * A `"combatants"` prompt (`choose_from_area` `count: "any"`, Logic Bomb) is the multi-pick: a
 * click toggles a ringed unit in or out of the pick, and the banner's CONFIRM answers with the
 * picked ids joined by `,` ({@link unitPickAnswer}), none included.
 */
import type { EncounterState } from "@lancer-vtt/domain";
import type { PromptRequest } from "@lancer-vtt/shared";
import type { TargetingBanner } from "./TargetingMode";

/** A board pick answering a `"combatant"` prompt (or, `multi`, a `"combatants"` one): its id and the candidate combatant ids. */
export type UnitPick = { promptId: string; ids: string[]; multi: boolean };

/** The {@link UnitPick} a prompt arms, or `null` when it is not a unit prompt. */
export function unitPickFor(prompt: PromptRequest): UnitPick | null {
  if (prompt.answerKind !== "combatant" && prompt.answerKind !== "combatants") return null;
  return { promptId: prompt.promptId, ids: prompt.options.map((o) => o.id), multi: prompt.answerKind === "combatants" };
}

/** `picked` with `id` toggled in or out, kept in candidate order. */
export function togglePicked(pick: UnitPick, picked: readonly string[], id: string): string[] {
  const next = picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id];
  return pick.ids.filter((c) => next.includes(c));
}

/** A multi-pick's answer: the picked ids joined by `,`, `""` for none. */
export function unitPickAnswer(picked: readonly string[]): string {
  return picked.join(",");
}

/** Every unit on the board the pick can't take: the ones that dim. */
export function unitPickDimmed(state: EncounterState, pick: UnitPick): Set<string> {
  return new Set(Object.keys(state.combatants).filter((id) => !pick.ids.includes(id)));
}

/**
 * The targeting banner while a unit pick is armed: the whole prompt, since no window opens for it.
 * `title` is the prompt's own sentence. A multi-pick names what is picked so far (`names`).
 */
export function unitPickBanner(args: { pick: UnitPick; title: string; names?: readonly string[] }): TargetingBanner {
  const n = args.pick.ids.length;
  if (args.pick.multi) {
    const names = args.names ?? [];
    return { kind: "attack", title: args.title, sub: `CLICK RINGED UNITS, THEN CONFIRM · PICKED: ${names.length > 0 ? names.join(", ").toUpperCase() : "NONE"}` };
  }
  return { kind: "attack", title: args.title, sub: `CLICK A RINGED UNIT · ${n} ${n === 1 ? "UNIT" : "UNITS"}` };
}
